import {
  CodeableConcept,
  DiagnosticReport,
  Encounter,
  Observation,
  Patient,
  QuestionnaireResponse,
} from '@medplum/fhirtypes';
import { MedplumClient } from '@medplum/core';
import { ReportResultValue } from '../../files-tab/utils/report-files.consts';

export type ListContentAction = 'edit' | 'remove';

// -----------------------------------------------------------------------------
// Birth Measurements Types
// -----------------------------------------------------------------------------

export interface BirthMeasurementsFormState {
  birthWeight: number | null; // in grams (gr)
  birthLength: number | null; // in centimeters (cm)
  birthHeadCircumference: number | null; // in centimeters (cm)
}

export interface BirthMeasurementsData {
  birthWeight: Observation | undefined;
  birthLength: Observation | undefined;
  birthHeadCircumference: Observation | undefined;
}

export interface BirthMeasurementsSaveParams {
  medplum: MedplumClient;
  patient: Patient;
  formState: BirthMeasurementsFormState;
  existingData?: BirthMeasurementsData;
}

// -----------------------------------------------------------------------------
// Prenatal Check Types (DiagnosticReport)
// -----------------------------------------------------------------------------

export type PrenatalCheckResultValue = 'Φυσιολογικό' | 'Παθολογικό';

export type PrenatalTestType =
  | 'Απλός καρυότυπος'
  | 'Μοριακός καρυότυπος'
  | 'WES'
  | 'Στοχευμένος έλεγχος';

export interface PrenatalCheckFormState {
  checkType: CodeableConcept | null; // From ValueSet vs-prebirth-checks-el
  reasonForTest: string; // q35
  testType: PrenatalTestType | null; // q36
  result: ReportResultValue | null; // q33
  observations: string; // q34
  date: string; // ISO date string
  file: File | null;
}

export interface PrenatalCheckModalProps {
  opened: boolean;
  patient: Patient;
  encounter?: Encounter | null;
  onClose: () => void;
  onSuccess?: () => void;
  report?: DiagnosticReport | null;
}

export interface PrenatalCheckSaveParams {
  medplum: MedplumClient;
  patient: Patient;
  encounterId?: string | null;
  formState: PrenatalCheckFormState;
  existingReport?: DiagnosticReport | null;
}

// -----------------------------------------------------------------------------
// Pre-implantation Check Types (DiagnosticReport)
// -----------------------------------------------------------------------------

export interface PreImplantationFormState {
  type: string; // q145 - Είδος
  date: string; // Date of the check
  result: ReportResultValue | null; // q146 - Αποτέλεσμα
  observations: string; // Observations when result is abnormal
  file: File | null; // PDF file attachment
}

export interface PreImplantationModalProps {
  opened: boolean;
  patient: Patient;
  encounter?: Encounter | null;
  onClose: () => void;
  onSuccess?: () => void;
  report?: DiagnosticReport | null;
}

export interface PreImplantationSaveParams {
  medplum: MedplumClient;
  patient: Patient;
  encounterId?: string | null;
  formState: PreImplantationFormState;
  existingReport?: DiagnosticReport | null;
}

// -----------------------------------------------------------------------------
// Prenatal Period Types (QuestionnaireResponse)
// -----------------------------------------------------------------------------

export type ConceptionMethod =
  | 'Φυσική σύλληψη'
  | 'Υποβοηθούμενη αναπαραγωγή'
  | 'Υιοθεσία';

export type AssistedReproductionMethod = 'IVF' | 'Παρένθετη μητέρα' | 'Άλλο';

export type BiologicalMaterialSource =
  | 'Βιολογικό υλικό των ίδιων'
  | 'Δότης σπέρματος'
  | 'Δότρια ωαρίων';

export type LivingConditionsUntilAdoption =
  | 'Κοινωνική δομή'
  | 'Διαβίωση σε βιολογική οικογενεια'
  | 'Άγνωστη';

export interface PrenatalPeriodFormState {
  conceptionMethod: ConceptionMethod | null; // q2
  assistedReproductionMethod: AssistedReproductionMethod | null; // q29
  biologicalMaterialSource: BiologicalMaterialSource | null; // q30
  adoptionAge: number | null; // q31
  otherMethod: string; // q32
  livingConditionsUntilAdoption: LivingConditionsUntilAdoption | null; // q141
  knownBiologicalRelativesHistory: boolean | null; // q142
}

export interface PrenatalPeriodModalProps {
  opened: boolean;
  patient: Patient;
  encounter?: Encounter | null;
  onClose: () => void;
  onSuccess?: () => void;
  questionnaireResponse?: QuestionnaireResponse | null;
}

// -----------------------------------------------------------------------------
// Perinatal Period Types (QuestionnaireResponse)
// -----------------------------------------------------------------------------

export type PerinatalPeriodStatus = 'ομαλή' | 'άλλο';
export type TwinPregnancy = 'Ναι' | 'Όχι';
export type DeliveryType = 'Φυσιολογικός Κολπικός' | 'Καισαρική τομή';
export type BirthClassification = 'IUGR' | 'SGA' | 'AGA' | 'LGA';

export interface PerinatalPeriodFormState {
  perinatalPeriod: PerinatalPeriodStatus | null; // prebirth-period
  complications: string; // q14
  deliveryNumber: number | null; // q13 - Τόκος
  gestationalAge: string; // q6 - format: "38+3"
  twinPregnancy: TwinPregnancy | null; // q147
  fetusRegression: boolean | null; // q148
  deliveryType: DeliveryType | null; // q7
  cesareanReason: string; // q8
  classification: BirthClassification | null; // q27
  maternityNotes: string; // q153
  file: File | null; // q154
}

export interface PerinatalPeriodModalProps {
  opened: boolean;
  patient: Patient;
  encounter?: Encounter | null;
  onClose: () => void;
  onSuccess?: () => void;
  questionnaireResponse?: QuestionnaireResponse | null;
}

// -----------------------------------------------------------------------------
// Hook Return Types
// -----------------------------------------------------------------------------

export interface UseBirthObservationsResult {
  data: BirthMeasurementsData;
  loading: boolean;
  error: Error | null;
  saveBirthMeasurements: (
    formState: BirthMeasurementsFormState
  ) => Promise<void>;
  deleteBirthMeasurements: () => Promise<void>;
  refetch: () => Promise<void>;
}

export interface UsePrenatalChecksResult {
  checks: DiagnosticReport[];
  loading: boolean;
  error: Error | null;
  addCheck: (formState: PrenatalCheckFormState) => Promise<DiagnosticReport>;
  updateCheck: (
    id: string,
    formState: PrenatalCheckFormState
  ) => Promise<DiagnosticReport>;
  deleteCheck: (id: string) => Promise<void>;
  refetch: () => Promise<void>;
}

export interface UsePreImplantationChecksResult {
  checks: DiagnosticReport[];
  loading: boolean;
  error: Error | null;
  addCheck: (formState: PreImplantationFormState) => Promise<DiagnosticReport>;
  updateCheck: (
    id: string,
    formState: PreImplantationFormState
  ) => Promise<DiagnosticReport>;
  deleteCheck: (id: string) => Promise<void>;
  refetch: () => Promise<void>;
}

// -----------------------------------------------------------------------------
// DiagnosticReport Category Codes
// -----------------------------------------------------------------------------

export const MEDICAL_HISTORY_REPORT_CATEGORIES = {
  PRENATAL_CHECK: 'prenatal-check',
  PRE_IMPLANTATION_CHECK: 'pre-implantation-check',
} as const;

// -----------------------------------------------------------------------------
// Prenatal Check Types (for conditional rendering)
// These correspond to codes in vs-prebirth-checks-el ValueSet
// -----------------------------------------------------------------------------

export const PRENATAL_CHECK_CODES_WITH_REASON = [
  'fetusmri',
  'emvriokardiografima',
  'lipsihoriakonlahnon',
  'amnioparakentisi',
  'miepemvatikoseleghos',
] as const;

export const PRENATAL_CHECK_CODES_WITH_TEST_TYPE = [
  'lipsihoriakonlahnon',
  'amnioparakentisi',
] as const;
